/**
 * ERC Floating Pin Validator Adapter for DRC System
 *
 * This validator checks for pins that are not connected to any net.
 *
 * Feature-gated via DrcEnabledCheckers.ErcFloatingPinValidator
 */
import { createTUID, getElementPartType, ReviewType } from "@buildwithflux/models";
import { getProblemTypeKeyFromReviewType } from "../../drc/types";
import { ElectricalPinType, fluxPinTypeToElectricalType } from "../pinTypes";
import { createErcProblem, getEffectivePinType, formatPinReference } from "./ercUtils";
/**
 * Checks if a pin type should be flagged when floating (not connected).
 */
function shouldFlagFloatingPin(pinType) {
    return pinType !== ElectricalPinType.NO_CONNECT && pinType !== ElectricalPinType.PASSIVE;
}
/**
 * Validator that checks for floating (unconnected) pins.
 */
export class ErcFloatingPinValidator {
    reviewType = ReviewType.DesignRuleCheckErcFloatingPin;
    problemTypeKey = getProblemTypeKeyFromReviewType(this.reviewType);
    defaultHidden = true;
    async checkForProblems(inputs) {
        const foundProblems = [];
        // Build a set of all connected terminal IDs from netMap
        const connectedTerminals = new Set();
        for (const terminals of Object.values(inputs.netMap)) {
            for (const terminalId of Object.keys(terminals)) {
                connectedTerminals.add(terminalId);
            }
        }
        // Check each element's terminals
        for (const [elementUid, element] of Object.entries(inputs.elements)) {
            const terminals = element.part_version_data_cache?.terminals;
            if (!terminals)
                continue;
            for (const [terminalUid, terminal] of Object.entries(terminals)) {
                const terminalId = createTUID([elementUid, terminalUid]);
                if (connectedTerminals.has(terminalId)) {
                    continue;
                }
                // Use getEffectivePinType to check user-modified pin type first
                const pinType = fluxPinTypeToElectricalType(getEffectivePinType(terminal));
                if (!shouldFlagFloatingPin(pinType)) {
                    continue;
                }
                const elementLabel = element.label;
                const elementName = element.part_version_data_cache?.name;
                const pinName = terminal.name;
                const pinNumber = terminal.pin_number;
                const elementPartType = getElementPartType(element);
                // Create a PinInfo-like object to use formatPinReference for consistent formatting
                const pinInfo = {
                    elementUid,
                    terminalUid,
                    terminalId,
                    pinType,
                    pinName,
                    pinNumber,
                    elementLabel,
                    elementName,
                    elementPartType,
                };
                // Use formatPinReference for consistent partDesignator:pinDesignator syntax
                const displayLabel = formatPinReference(pinInfo);
                foundProblems.push(createErcProblem(this.problemTypeKey, `${this.problemTypeKey}_${elementUid}_${terminalUid}`, `Floating pin: ${displayLabel}`, [elementUid], [displayLabel]));
            }
        }
        return {
            error: false,
            problemTypeKey: this.problemTypeKey,
            foundProblems,
        };
    }
}
//# sourceMappingURL=ErcFloatingPinValidator.js.map